import { useEffect, useMemo, useState } from 'react';
import type {
  Address,
  CatalogResponse,
  CurrentUser,
  Order,
  PublicProduct,
  PublicSettings,
  SlotsResponse,
  VendorPublic,
  ZoneBrief,
} from '@arbuz/shared';
import { ApiError, api } from '../api';
import { estimateTotal, useCart } from '../cart';
import { formatMoney, makeT } from '../i18n';
import { haptic } from '../tg';
import {
  BottomAction,
  ClientTopBar,
  StepIndicator,
} from './clientUi';
import { estimateWeight, idempotencyKey } from './checkout/helpers';
import { StepAddressTime } from './checkout/StepAddressTime';
import { StepQuantity } from './checkout/StepQuantity';
import { StepReview } from './checkout/StepReview';
import { StepPayment } from './checkout/StepPayment';

/** Каталог витрины отдаётся вместе с зоной по адресу клиента. */
type CatalogWithZone = CatalogResponse & { zone: ZoneBrief | null };

type Step = 1 | 2 | 3 | 4;

export function Checkout({
  user,
  vendor,
  onDone,
  onNeedAddress,
  onNeedVendor,
}: {
  user: CurrentUser;
  /** Исполнитель, у которого оформляется заказ: его каталог, слоты и цена доставки. */
  vendor: VendorPublic;
  onDone: (order: Order) => void;
  onNeedAddress: () => void;
  onNeedVendor: () => void;
}) {
  const t = makeT(user.locale);
  const cart = useCart();

  const [step, setStep] = useState<Step>(1);
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [addressId, setAddressId] = useState<number | null>(null);
  const [slots, setSlots] = useState<SlotsResponse | null>(null);
  const [slotDate, setSlotDate] = useState<string | null>(null);
  const [slotStart, setSlotStart] = useState<string | null>(null);
  const [payment, setPayment] = useState<'cash' | 'card_transfer'>('cash');
  const [comment, setComment] = useState('');
  const [products, setProducts] = useState<Map<number, PublicProduct>>(new Map());
  /** Зона выбранного исполнителя по адресу клиента: она задаёт цену доставки. */
  const [zone, setZone] = useState<ZoneBrief | null>(null);
  /** Реквизиты для перевода — карта АДМИНИСТРАТОРА, не исполнителя. */
  const [requisites, setRequisites] = useState<PublicSettings | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    const translate = makeT(user.locale);

    void Promise.all([
      api.get<Address[]>('/api/addresses'),
      // Слоты и каталог принадлежат витрине: рабочие часы, выходные и
      // вместимость у каждого исполнителя свои.
      api.get<SlotsResponse>(`/api/vendors/${vendor.id}/slots`),
      api.get<CatalogWithZone>(`/api/vendors/${vendor.id}/catalog`),
      api.get<PublicSettings>('/api/settings/public'),
    ])
      .then(([a, s, c, settings]) => {
        if (cancelled) return;
        setAddresses(a);
        setAddressId(a.find((x) => x.is_default)?.id ?? a[0]?.id ?? null);
        setSlots(s);
        setRequisites(settings);

        const firstAvailable = s.days.flatMap((day) =>
          day.slots.filter((slot) => slot.available).map((slot) => ({ day: day.date, slot })),
        )[0];
        if (firstAvailable) {
          setSlotDate(firstAvailable.day);
          setSlotStart(firstAvailable.slot.start);
        }

        const map = new Map<number, PublicProduct>();
        for (const cat of c.categories) for (const p of cat.products) map.set(p.id, p);
        setProducts(map);
        setZone(c.zone);
        setError(null);
      })
      .catch(() => {
        if (!cancelled) setError(translate('loadError'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [user.locale, vendor.id]);

  const address = addresses.find((a) => a.id === addressId) ?? null;
  const selectedDay = slots?.days.find((day) => day.date === slotDate) ?? null;
  const selectedSlot = selectedDay?.slots.find((slot) => slot.start === slotStart) ?? null;
  const knownLines = cart.lines.filter((line) => products.has(line.product_id));
  const missingLines = products.size > 0 ? cart.lines.filter((line) => !products.has(line.product_id)) : [];
  const itemsTotal = estimateTotal(knownLines, products);
  const delivery = zone ? Number(zone.delivery_price) : Number(vendor.delivery_price);
  const displayTotal = itemsTotal + delivery;
  const itemCount = cart.lines.reduce((sum, line) => sum + line.qty, 0);
  const weight = useMemo(() => estimateWeight(knownLines, products), [knownLines, products]);
  const canStep1 = Boolean(address && slotDate && slotStart && selectedSlot?.available);
  const canStep2 = knownLines.length > 0 && missingLines.length === 0;

  async function refreshSlots(): Promise<void> {
    try {
      setSlots(await api.get<SlotsResponse>(`/api/vendors/${vendor.id}/slots`));
    } catch {
      // Основная ошибка уже показана пользователю при submit.
    }
  }

  async function submit() {
    if (!addressId || !slotDate || !slotStart || knownLines.length === 0) return;
    setBusy(true);
    setError(null);

    try {
      const created = await api.post<Order>(
        '/api/orders',
        {
          vendor_id: vendor.id,
          items: knownLines,
          address_id: addressId,
          slot_date: slotDate,
          slot_start: slotStart,
          payment_method: payment,
          comment: comment.trim() || undefined,
        },
        { 'Idempotency-Key': idempotencyKey(vendor.id, knownLines, slotDate, slotStart) },
      );

      haptic('success');
      cart.clear();
      onDone(created);
    } catch (err) {
      haptic('error');
      setError(err instanceof ApiError ? err.message : t('loadError'));
      if (err instanceof ApiError && err.code === 'SLOT_UNAVAILABLE') {
        setStep(1);
        void refreshSlots();
      }
      if (err instanceof ApiError && err.code === 'PRODUCT_UNAVAILABLE') {
        setStep(2);
      }
      // Исполнитель выключил витрину или перестал возить по этому адресу,
      // пока клиент оформлял заказ: выбирать надо заново, а не «повторить»
      if (
        err instanceof ApiError &&
        (err.code === 'VENDOR_UNAVAILABLE' || err.code === 'ZONE_NOT_COVERED')
      ) {
        onNeedVendor();
      }
    } finally {
      setBusy(false);
    }
  }

  function next() {
    haptic('light');
    if (step === 1 && !canStep1) return;
    if (step === 2 && !canStep2) return;
    if (step === 4) {
      void submit();
      return;
    }
    setStep((current) => (Math.min(4, current + 1) as Step));
  }

  if (loading) {
    return (
      <main className="client-page">
        <p className="muted">…</p>
      </main>
    );
  }

  if (addresses.length === 0) {
    return (
      <main className="client-page">
        <ClientTopBar title={t('checkout')} />
        <h1 className="h1">{t('addressTitle')}</h1>
        <div className="client-card" style={{ padding: 'var(--s-4)', marginTop: 'var(--s-4)' }}>
          <p style={{ margin: 0 }}>{t('noAddressYet')}</p>
        </div>
        <button
          type="button"
          className="btn-primary"
          style={{ width: '100%', marginTop: 'var(--s-4)' }}
          onClick={onNeedAddress}
        >
          {t('addAddress')}
        </button>
      </main>
    );
  }

  const buttonDisabled =
    busy || (step === 1 && !canStep1) || (step === 2 && !canStep2) || (step === 4 && !canStep2);

  return (
    <main className="client-page with-bottom-bar">
      <ClientTopBar
        title={t('checkout')}
        onBack={() => {
          if (step === 1) return;
          setStep((current) => (Math.max(1, current - 1) as Step));
        }}
      />
      <StepIndicator current={step} />

      {step === 1 && (
        <StepAddressTime
          user={user}
          addresses={addresses}
          addressId={addressId}
          slots={slots}
          slotDate={slotDate}
          slotStart={slotStart}
          onAddress={setAddressId}
          onNeedAddress={onNeedAddress}
          onSlot={(date, start) => {
            setSlotDate(date);
            setSlotStart(start);
          }}
        />
      )}

      {step === 2 && (
        <StepQuantity
          user={user}
          lines={cart.lines}
          products={products}
          missingCount={missingLines.length}
          weight={weight}
          comment={comment}
          onQty={(productId, qty) => {
            haptic('soft');
            cart.setQty(productId, qty);
          }}
          onComment={setComment}
        />
      )}

      {step === 3 && (
        <StepReview
          user={user}
          vendor={vendor}
          address={address}
          slotDate={slotDate}
          slotStart={slotStart}
          selectedSlotEnd={selectedSlot?.end ?? null}
          itemCount={itemCount}
          weight={weight}
          comment={comment}
          total={displayTotal}
        />
      )}

      {step === 4 && (
        <StepPayment
          user={user}
          payment={payment}
          total={displayTotal}
          itemCount={itemCount}
          requisites={requisites}
          onPayment={setPayment}
        />
      )}

      {error && (
        <p role="alert" style={{ color: 'var(--c-danger)', fontSize: 'var(--fs-sm)' }}>
          {error}
        </p>
      )}

      <BottomAction>
        {step < 4 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 'var(--s-3)' }}>
            <span className="muted">{t('total')}</span>
            <strong>
              {formatMoney(String(displayTotal), user.locale)} {t('sum')}
            </strong>
          </div>
        )}
        <button
          type="button"
          className="btn-primary"
          style={{ width: '100%' }}
          disabled={buttonDisabled}
          onClick={next}
        >
          {busy ? t('saving') : step === 4 ? t('confirmOrder') : t('next')}
        </button>
      </BottomAction>
    </main>
  );
}

